import * as React from "react";
import { Alert, Radio, Tooltip, Input, Row, Col, Checkbox, Button, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { randomNumberInRange } from "utils/randomNumberInRange";
import i18nConfig, { I18N_NS } from "Config/i18n";
import { numberInRange, inputLengthInRange } from "utils/inputValidation";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { projectsDataStore } from "Stores";
import { TaskAccessErrorMode, taskAttachmentsStore } from "Pages/Project/Tasks/Stores/TaskAttachmentsStore";
import { taskDataStore } from "Pages/Project/Tasks/Stores";
import { taskSubmitStore, TaskSubmitStoreType } from "Pages/Project/Tasks/Stores/TaskSubmitStore";
import { isEmpty } from "lodash";

/**
 * ! The last major changes was at July 2021
 * =========================================
 * ! For now, this component handle access rights for communications & tasks
 * ! with their specific functions and conditions
 * =========================================
 * ! When adding additional modules, functions or actions, it is recommended to refactor the component.
 */

interface AccessRightsFormProps extends WithTranslation {
  onSubmit: (data: object) => void;
  onClose: () => void;
  isLoading?: boolean;
  hintText?: string | React.ReactNode;
  problemFiles?: { withoutViewer: string[]; viewerOnly: string[] };
  accessType: "PIN" | "ACCESS_TYPE";
  fromModule: "TASK" | "MAIL";
  disableZip?: boolean;
}

export const AccessRightsForm = withTranslation(I18N_NS.PROJECT)(
  class extends React.Component<AccessRightsFormProps, { attachmentType: API_MAILREQUEST_ATTACHMENTTYPE }> {
    readonly randomPin = randomNumberInRange(1000, 99999);
    constructor(props: AccessRightsFormProps) {
      super(props);
      this.state = {
        attachmentType: "LINK",
      };
    }

    handleFinish = async (values) => {
      const { attachmentType } = this.state;
      if (isEmpty(values) && attachmentType === "FILE") {
        this.props.onSubmit({ attachmentType });
      } else {
        if (values.accessLevel.toLowerCase().indexOf("remove") !== -1) {
          if (values.accessLevel === "REMOVE_ATTACH") {
            taskAttachmentsStore.newAttachFileIds.forEach((id) => {
              taskAttachmentsStore.deleteFile(id);
            });
            this.props.onSubmit({ attachments: [] });
          } else if (values.accessLevel === "REMOVE_COMMENT") {
            console.warn("comment removed");
          } else if (values.accessLevel === "REMOVE_ASSIGNEE") {
            taskSubmitStore.clearAll();
            taskSubmitStore.update(taskDataStore.data as TaskSubmitStoreType);
            taskAttachmentsStore.addInitialFiles(taskDataStore.data.attachments);
          }
        } else {
          if (attachmentType === "LINK" && values.accessLevel !== "REMOVE_ATTACH") {
            if (taskAttachmentsStore.accessErrorMode === "COMMENT") {
              this.props.onSubmit({ attachmentSettings: values });
            } else {
              this.props.onSubmit({ attachmentSettings: values, attachmentType });
            }
          } else {
            this.props.onSubmit({ attachmentType });
          }
        }
      }

      this.props.onClose();
    };

    inputRangeValidator = (rule, value, cb) => {
      const { t } = this.props;
      const maxNumber = 10;
      if (typeof value !== "number") {
        return cb(i18nConfig.t("workflow:::Must be a integer"));
      }
      if (value <= 0 || value > maxNumber) {
        return cb(t("Must be lower than {{maxNumber}}", { maxNumber }));
      }
      cb();
      const message = t("Must be lower than {{maxNumber}}", { maxNumber });
      return { message };
    };

    handleTooltipText = (files: { withoutViewer: string[]; viewerOnly: string[] }) => {
      const { t } = this.props;
      const withoutViewerText = files.withoutViewer?.length
        ? t(`There is no viewer available for the following files: {{list}}`, { list: files.withoutViewer.join(", ") })
        : "";
      const viewerOnlyText = files.viewerOnly?.length
        ? t(
            // eslint-disable-next-line max-len
            "The files: {{list}} cannot be viewed by these recipients (be aware that Office Online technically needs read access).",
            { list: files.viewerOnly.join(", ") }
          )
        : "";
      return (
        <div>
          {withoutViewerText && (
            <div>
              {withoutViewerText}
              <br />
            </div>
          )}
          {viewerOnlyText && <div>{viewerOnlyText}</div>}
        </div>
      );
    };
    render() {
      const { t, fromModule, accessType, hintText, onClose, isLoading, problemFiles } = this.props;
      const errorFilesLength = problemFiles && (problemFiles.viewerOnly.length || problemFiles.withoutViewer.length);
      const radioText = (mode: TaskAccessErrorMode) => {
        const errorsMap: { [key in TaskAccessErrorMode]: string } = {
          ASSIGNEE: t("Do not change assignee"),
          ATTACH: t("Do not attach new files"),
          COMMENT: t("Do not add comment"),
        };
        return errorsMap[mode];
      };
      return (
        <div>
          {fromModule === "MAIL" &&
            projectsDataStore.currentProject.mailSettings.includeAttachment &&
            accessType === "PIN" && (
              <Row align="middle" className="mb-md">
                {t("Give access to internal project files via")}:
                <Radio.Group
                  value={this.state.attachmentType}
                  className="ml-md"
                  size="small"
                  buttonStyle="solid"
                  onChange={(e) => this.setState({ attachmentType: e.target.value })}
                >
                  <Radio.Button value="LINK">{t("Guest access page")}</Radio.Button>
                  <Radio.Button disabled={this.props.disableZip} value="FILE">
                    {t("Include files as zip")}
                  </Radio.Button>
                </Radio.Group>
              </Row>
            )}
          <Form onFinish={this.handleFinish} layout="vertical">
            {(fromModule === "TASK" || this.state.attachmentType === "LINK") && (
              <>
                {hintText && <Alert message={t("Note")} showIcon={true} type="info" description={hintText} />}
                <Form.Item name="accessLevel" initialValue="DOWNLOAD">
                  <Radio.Group>
                    <Radio value={"DOWNLOAD"}>
                      {t("Grant reading permission for selected files")}
                      <Tooltip
                        title={t(
                          // eslint-disable-next-line max-len
                          "Recipients can download the files. This right will only be granted for files that you are allowed to download yourself."
                        )}
                      >
                        <FontAwesomeIcon
                          fixedWidth={true}
                          size="lg"
                          className={"text-info"}
                          style={{ marginLeft: 5 }}
                          icon={["far", "info-circle"]}
                        />
                      </Tooltip>
                    </Radio>
                    {fromModule === "MAIL" && (
                      <Radio value={"VIEW_ONLY"}>
                        {t("Grant only permission to display file (no download) for selected files")}
                        <Tooltip
                          title={t(
                            // eslint-disable-next-line max-len
                            "The user will be informed about existence of the file but cannot download it. If an internal online-viewer is activated, file can still be viewed online"
                          )}
                        >
                          <FontAwesomeIcon
                            fixedWidth={true}
                            size="lg"
                            className={"text-info"}
                            style={{ marginLeft: 5 }}
                            icon={["far", "info-circle"]}
                          />
                        </Tooltip>
                        {errorFilesLength ? (
                          <Tooltip title={this.handleTooltipText(problemFiles)}>
                            <FontAwesomeIcon
                              fixedWidth={true}
                              size="lg"
                              className={"text-warning"}
                              style={{ marginLeft: 5 }}
                              icon={["far", "exclamation-circle"]}
                            />
                          </Tooltip>
                        ) : null}
                      </Radio>
                    )}
                    {fromModule === "TASK" && (
                      <Radio value={`REMOVE_${taskAttachmentsStore.accessErrorMode}`}>
                        {radioText(taskAttachmentsStore.accessErrorMode)}
                      </Radio>
                    )}
                  </Radio.Group>
                </Form.Item>
                {accessType === "PIN" && (
                  <Row gutter={20}>
                    <Col sm={12}>
                      <Form.Item
                        label={t("Access PIN")}
                        help={t("Must be between {{minNumber}} and {{maxNumber}}", { minNumber: 4, maxNumber: 20 })}
                        rules={[{ required: true, validator: inputLengthInRange, min: 4, max: 20 }]}
                        initialValue={this.randomPin}
                        name="pin"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col sm={12}>
                      <Form.Item
                        name="pinDuration"
                        label={t("Validity of access")}
                        help={`1 - 90 ${t("days")}`}
                        initialValue={projectsDataStore.currentProject?.infoMailSettings.mailDefaultValidityOfAccess}
                        rules={[{ required: true, validator: numberInRange, min: 1, max: 90 }]}
                      >
                        <Input suffix={t("days")} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name="sendPin" valuePropName="checked" initialValue={true}>
                        <Checkbox>{t("Include PIN in email")}</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </>
            )}
            <ButtonsRow justify="space-between">
              <Col>
                <Button onClick={onClose}>{t("Cancel")}</Button>
              </Col>
              <Col>
                <Button loading={isLoading} type="primary" htmlType="submit">
                  {t("Submit")}
                </Button>
              </Col>
            </ButtonsRow>
          </Form>
        </div>
      );
    }
  }
);
